import React, { useState, useRef, useEffect } from 'react';

interface Provider {
  name: string;
  value: string;
  logo: string;
  description: string;
  requiredConfig: string[];
}

interface ProviderSelectorProps {
  selectedProvider: string;
  onProviderChange: (provider: string) => void;
  providers: Provider[];
  placeholder?: string;
}

export const ProviderSelector: React.FC<ProviderSelectorProps> = ({
  selectedProvider,
  onProviderChange,
  providers,
  placeholder = "Selecione um provedor"
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMenuOpen, setSearchMenuOpen] = useState(false);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedProviderObject = providers.find(
    (provider) => provider.value === selectedProvider
  );

  useEffect(() => {
    const filtered = providers.filter((provider) =>
      provider.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProviders(filtered);
  }, [searchQuery, providers]);

  const handleProviderSelect = (providerValue: string) => {
    setSearchQuery('');
    onProviderChange(providerValue);
    setSearchMenuOpen(false);
  };

  const handleXButton = () => {
    if (searchQuery.length > 0) {
      setSearchQuery('');
      if (searchInputRef.current) searchInputRef.current.value = '';
    } else {
      setSearchMenuOpen(!searchMenuOpen);
    }
  };

  return (
    <div className="relative">
      {searchMenuOpen && (
        <div
          className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-70 backdrop-blur-sm z-10"
          onClick={() => setSearchMenuOpen(false)}
        />
      )}
      
      {searchMenuOpen ? (
        <div className="absolute top-0 left-0 w-full max-w-[640px] max-h-[310px] min-h-[64px] bg-white rounded-lg flex flex-col justify-between cursor-pointer border-2 border-blue-500 z-20 shadow-lg">
          <div className="w-full flex flex-col gap-y-1">
            <div className="flex items-center sticky top-0 z-10 border-b border-gray-300 mx-4 bg-white">
              <svg className="absolute left-4 z-30 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                autoComplete="off"
                placeholder="Buscar provedores"
                className="border-none ml-4 my-2 bg-transparent z-20 pl-8 h-[38px] w-full px-4 py-1 text-sm outline-none text-gray-900 placeholder:text-gray-500 placeholder:font-medium"
                onChange={(e) => setSearchQuery(e.target.value)}
                ref={searchInputRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              />
              <button
                onClick={handleXButton}
                className="cursor-pointer text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 pl-4 pr-2 flex flex-col gap-y-1 overflow-y-auto pb-4 max-h-[245px]">
              {filteredProviders.map((provider) => (
                <button
                  key={provider.name}
                  onClick={() => handleProviderSelect(provider.value)}
                  className={`border-none hover:cursor-pointer hover:bg-gray-100 flex gap-x-2 items-center p-2 rounded-md w-full text-left ${
                    selectedProvider === provider.value ? "bg-blue-50" : ""
                  }`}
                >
                  <img
                    src={provider.logo}
                    alt={`${provider.name} logo`}
                    className="w-6 h-6 rounded-md"
                  />
                  <div className="flex flex-col">
                    <div className="text-xs text-gray-900 font-medium">{provider.name}</div>
                    <div className="text-xs text-gray-500">{provider.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <button
          className="w-full max-w-[640px] h-[64px] bg-white rounded-lg flex items-center p-[14px] justify-between cursor-pointer border-2 border-transparent hover:border-blue-500 transition-all duration-300 shadow-sm"
          type="button"
          onClick={() => setSearchMenuOpen(true)}
        >
          <div className="flex gap-x-4 items-center">
            <img
              src={selectedProviderObject?.logo || '/know-logo.png'}
              alt={`${selectedProviderObject?.name} logo`}
              className="w-10 h-10 rounded-md"
            />
            <div className="flex flex-col text-left">
              <div className="text-sm font-semibold text-gray-900">
                {selectedProviderObject?.name || "Nenhum selecionado"}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {selectedProviderObject?.description || placeholder}
              </div>
            </div>
          </div>
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
};



